import { useState } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { TicketPriority } from '@/mocks/adminSupport';
import { priorityMeta, allPriorities } from '@/mocks/adminSupport';

interface TicketFormModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (subject: string, description: string, priority: TicketPriority) => void;
}

export default function TicketFormModal({ open, onClose, onCreate }: TicketFormModalProps) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');

  if (!open) return null;

  const handleSubmit = () => {
    if (!subject.trim()) {
      SwalAlert.warning('Asunto requerido', 'Por favor escribí el asunto del ticket.');
      return;
    }
    if (!description.trim()) {
      SwalAlert.warning('Descripción requerida', 'Por favor describí el problema o consulta.');
      return;
    }
    onCreate(subject.trim(), description.trim(), priority);
  };

  const handleClose = () => {
    setSubject('');
    setDescription('');
    setPriority('normal');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-lg overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center">
              <i className="ri-add-line text-primary-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">Crear ticket de soporte</h3>
              <p className="text-foreground-400 text-[10px]">Completá los datos para abrir un nuevo ticket</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Asunto */}
          <div>
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Asunto <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Resumen del problema o consulta"
              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
            />
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Descripción <span className="text-red-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describí el problema con el mayor detalle posible..."
              rows={4}
              maxLength={500}
              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all resize-none"
            ></textarea>
            <p className="text-[10px] text-foreground-400 mt-1 text-right">{description.length}/500</p>
          </div>

          {/* Prioridad */}
          <div>
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-2">
              Prioridad <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {allPriorities.map((p) => {
                const meta = priorityMeta[p];
                const isActive = priority === p;
                return (
                  <button
                    key={p}
                    onClick={() => setPriority(p)}
                    className={`px-3.5 py-2 rounded-xl text-[11px] font-bold transition-all cursor-pointer whitespace-nowrap border ${
                      isActive
                        ? `${meta.bgColor} ${meta.color} border-current/30`
                        : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
                      {meta.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200">
          <button
            onClick={handleClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            <span className="flex items-center gap-1.5">
              <i className="ri-add-line text-base"></i>
              Crear ticket
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}